import { useEffect } from "react";
import { useSession } from "../../auth/useAuth.ts";
import { useStore } from "../../stores/store.ts";
import { dismissToast, toastStore } from "../../stores/toasts.ts";
import { resumeWatches } from "../../trade/orderWatch.ts";
import { Toast } from "../../ui/Toast.tsx";
import s from "./shell.module.css";

/** The toast host. One per app, mounted by the shell, so a toast survives a route change — an order submitted
 *  on a token page must still be able to report its outcome after the user navigates away. */
export function Toasts() {
  const toasts = useStore(toastStore);
  const session = useSession();

  /**
   * ⚠️ THE RESOLVER IS RESUMED WHERE THE NOTICES ARE RENDERED, AND THAT PLACEMENT IS THE POINT.
   *
   * The host's own comment above promises that a notice outlives the page that raised it. Until 2026-08-16
   * the only thing that could RESOLVE one lived in `TradePanel`, so the promise held for the spinner and not
   * for the answer: navigate away or reload mid-confirm and the notice span for the rest of the session.
   * Mounting the resume here means the thing that renders a pending notice and the thing that ends it have
   * the same lifetime, which is the invariant that was missing.
   *
   * GATED ON A SESSION because `GET /intents` is authenticated — resuming before the boot has verified the
   * bearer would spend a guaranteed 401 and sign the user out of a tab that is signed in. `resume()` is
   * idempotent, so re-running it on a session transition re-announces nothing.
   */
  useEffect(() => {
    if (session.status === "signedIn") resumeWatches();
  }, [session.status]);

  useEffect(() => {
    // One timer per toast with a TTL. A null `ttlMs` is STICKY on purpose: a pending trade toast is replaced
    // by its result (same key), never expired on a clock — a swap that takes 40 s must not have its
    // "Submitted — confirming" notice vanish at 5 s, leaving the user with no record that anything is in
    // flight. ⚠️ THAT PRIVILEGE IS NOW PINNED to the one module that can honour it — see `structure.test.ts`
    // rule 8: raising a sticky notice with no resolver is what produced the eternal spinner.
    const timers = toasts
      .filter((t) => t.ttlMs !== null)
      .map((t) => setTimeout(() => dismissToast(t.key), t.ttlMs as number));
    return () => {
      for (const timer of timers) clearTimeout(timer);
    };
  }, [toasts]);

  if (toasts.length === 0) return null;
  return (
    <div className={s.toastHost}>
      {toasts.map((toast) => (
        <Toast key={toast.key} toast={toast} onDismiss={dismissToast} />
      ))}
    </div>
  );
}
